import { BigNumber } from "bignumber.js";
import { z } from "zod";
import { TAX_RATE } from "~/constants/AppConst";
import {
	calculateConstructionAmount,
	ESTIMATION_TYPE
} from "~/models/DealConstruction";
import type { DealEstimationOcrRequestItem } from "~/models/DealEstimationOcrRequest";
import { removeNewlines } from "~/utils/StringUtil";

export const DEAL_ESTIMATION_OCR_MAX_NOTE_LENGTH = 400;
export const DEAL_ESTIMATION_OCR_MIN_CHARGE_PER = 0;
export const DEAL_ESTIMATION_OCR_MAX_CHARGE_PER = 100;
export const DEAL_ESTIMATION_OCR_TOTAL_CHARGE_PER = 100;
export const DEAL_ESTIMATION_OCR_MIN_UNIT = 1;
export const DEAL_ESTIMATION_OCR_TOTAL_CHARGE_PER_ERROR_MESSAGE =
	"合計が100になるように入力してください";

const nullableShortTextSchema = z
	.string()
	.transform((value) => {
		const trimmedValue = value.trim();
		return trimmedValue === "" ? null : trimmedValue;
	})
	.pipe(z.string().max(255, "255文字以内で入力してください").nullable());

const requiredShortTextSchema = z
	.string()
	.transform((value) => value.trim())
	.pipe(
		z
			.string()
			.min(1, "入力してください")
			.max(255, "255文字以内で入力してください")
	);

const requiredNumberSchema = (numberSchema: z.ZodNumber) =>
	z
		.number({ error: "入力してください" })
		.nullable()
		.pipe(numberSchema.nullable())
		.refine((value): boolean => value !== null, "入力してください");

const chargePerSchema = requiredNumberSchema(
	z
		.number()
		.int("整数で入力してください")
		.min(DEAL_ESTIMATION_OCR_MIN_CHARGE_PER, "0以上で設定してください")
		.max(DEAL_ESTIMATION_OCR_MAX_CHARGE_PER, "100以下で設定してください")
);

export const dealEstimationOcrFormItemSchema = z
	.object({
		companyChargePer: chargePerSchema,
		estimationType: z.enum(Object.values(ESTIMATION_TYPE)),
		note: z
			.string()
			.transform((value) => (value === "" ? null : value))
			.refine(
				(value) =>
					value === null ||
					removeNewlines(value).length <= DEAL_ESTIMATION_OCR_MAX_NOTE_LENGTH,
				`${DEAL_ESTIMATION_OCR_MAX_NOTE_LENGTH}文字以内で入力してください`
			),
		otherName: requiredShortTextSchema,
		ownerChargePer: chargePerSchema,
		point: nullableShortTextSchema,
		renteeChargePer: chargePerSchema,
		unit: requiredNumberSchema(
			z.number().min(DEAL_ESTIMATION_OCR_MIN_UNIT, "1以上で設定してください")
		),
		unitAmount: requiredNumberSchema(z.number().int("整数で入力してください")),
		unitName: requiredShortTextSchema
	})
	.superRefine((item, context) => {
		const filledKeys = CHARGE_PER_KEYS.filter((key) => item[key] !== null);
		if (filledKeys.length >= 2) {
			const total = filledKeys.reduce((sum, key) => sum + (item[key] ?? 0), 0);
			const isAllFilled = filledKeys.length === CHARGE_PER_KEYS.length;
			const hasTotalChargePerError = isAllFilled
				? total !== DEAL_ESTIMATION_OCR_TOTAL_CHARGE_PER
				: total > DEAL_ESTIMATION_OCR_TOTAL_CHARGE_PER;

			if (hasTotalChargePerError) {
				// メッセージが3つ並ぶと冗長なため、代表して借主負担%にのみ設定する
				context.addIssue({
					code: "custom",
					message: DEAL_ESTIMATION_OCR_TOTAL_CHARGE_PER_ERROR_MESSAGE,
					path: ["renteeChargePer"]
				});
			}
		}
	});

export const dealEstimationOcrFormSchema = z.object({
	items: z.array(dealEstimationOcrFormItemSchema)
});

export type DealEstimationOcrFormItem = z.input<
	typeof dealEstimationOcrFormItemSchema
>;

export type DealEstimationOcrFormInput = z.input<
	typeof dealEstimationOcrFormSchema
>;

export type DealEstimationOcrFormValues = z.output<
	typeof dealEstimationOcrFormSchema
>;

export type DealEstimationOcrAmounts = {
	nonProposalTotalAmount: number;
	proposalTotalAmount: number;
	totalAmount: number;
	totalAmountWithTax: number;
};

export type ChargePers = Pick<
	DealEstimationOcrRequestItem,
	"companyChargePer" | "ownerChargePer" | "renteeChargePer"
>;

export const CHARGE_PER_KEYS = [
	"companyChargePer",
	"ownerChargePer",
	"renteeChargePer"
] as const satisfies readonly (keyof ChargePers)[];

/**
 * 負担割合のうち未入力の項目を、合計が100になるように補完する。
 * 未入力が1つの場合は残りの合計から算出し、補完値が0〜100の範囲外になるときは
 * 入力自体が誤っているため補完しない。未入力が2つ以上でも、入力済みの合計が
 * 既に100に達している場合は残りが0以外あり得ないため0で補完する。
 */
export const complementChargePers = (chargePers: ChargePers): ChargePers => {
	const missingKeys = CHARGE_PER_KEYS.filter((key) => chargePers[key] === null);
	if (missingKeys.length === 0) {
		return chargePers;
	}

	const filledTotal = CHARGE_PER_KEYS.reduce(
		(total, key) => total + (chargePers[key] ?? 0),
		0
	);

	if (missingKeys.length === 1) {
		const [missingKey] = missingKeys;
		const complemented = DEAL_ESTIMATION_OCR_TOTAL_CHARGE_PER - filledTotal;
		if (
			complemented < DEAL_ESTIMATION_OCR_MIN_CHARGE_PER ||
			complemented > DEAL_ESTIMATION_OCR_MAX_CHARGE_PER
		) {
			return chargePers;
		}

		return { ...chargePers, [missingKey]: complemented };
	}

	if (filledTotal === DEAL_ESTIMATION_OCR_TOTAL_CHARGE_PER) {
		return {
			...chargePers,
			...Object.fromEntries(
				missingKeys.map((key) => [key, DEAL_ESTIMATION_OCR_MIN_CHARGE_PER])
			)
		};
	}

	return chargePers;
};

export const buildDealEstimationOcrFormValues = (
	items: DealEstimationOcrRequestItem[]
): DealEstimationOcrFormInput => ({
	items: items.map((item) => {
		const complementedChargePers = complementChargePers({
			companyChargePer: item.companyChargePer,
			ownerChargePer: item.ownerChargePer,
			renteeChargePer: item.renteeChargePer
		});
		return {
			companyChargePer: complementedChargePers.companyChargePer,
			estimationType: ESTIMATION_TYPE.NONE,
			note: item.note ?? "",
			otherName: item.otherName,
			ownerChargePer: complementedChargePers.ownerChargePer,
			point: item.point ?? "",
			renteeChargePer: complementedChargePers.renteeChargePer,
			unit: item.unit,
			unitAmount: item.unitAmount,
			unitName: item.unitName ?? ""
		};
	})
});

export const buildEmptyDealEstimationOcrFormItem =
	(): DealEstimationOcrFormItem => ({
		companyChargePer: null,
		estimationType: ESTIMATION_TYPE.NONE,
		note: "",
		otherName: "",
		ownerChargePer: null,
		point: "",
		renteeChargePer: null,
		unit: null,
		unitAmount: null,
		unitName: ""
	});

export const calculateDealEstimationOcrItemAmount = (
	item: Pick<DealEstimationOcrFormItem, "unit" | "unitAmount">
): number | null => {
	if (
		item.unit === null ||
		item.unitAmount === null ||
		!Number.isFinite(item.unit) ||
		!Number.isFinite(item.unitAmount)
	) {
		return null;
	}

	return calculateConstructionAmount(item.unit, item.unitAmount);
};

export const calculateDealEstimationOcrAmounts = (
	items: DealEstimationOcrFormItem[]
): DealEstimationOcrAmounts => {
	const { nonProposalTotalAmount, proposalTotalAmount } = items.reduce(
		(acc, item) => {
			const amount = calculateDealEstimationOcrItemAmount(item) ?? 0;
			if (item.estimationType === ESTIMATION_TYPE.PROPOSAL) {
				return {
					...acc,
					proposalTotalAmount: BigNumber(acc.proposalTotalAmount)
						.plus(amount)
						.toNumber()
				};
			}
			return {
				...acc,
				nonProposalTotalAmount: BigNumber(acc.nonProposalTotalAmount)
					.plus(amount)
					.toNumber()
			};
		},
		{ nonProposalTotalAmount: 0, proposalTotalAmount: 0 }
	);

	const totalAmount = BigNumber(nonProposalTotalAmount)
		.plus(proposalTotalAmount)
		.toNumber();
	const tax = BigNumber(totalAmount)
		.multipliedBy(TAX_RATE)
		.integerValue(BigNumber.ROUND_DOWN)
		.toNumber();

	return {
		nonProposalTotalAmount,
		proposalTotalAmount,
		totalAmount,
		totalAmountWithTax: BigNumber(totalAmount).plus(tax).toNumber()
	};
};
